import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import CareFlow from './CareFlow'
import { Container } from './sections'
import { getConditionBySlug } from './conditionsData'

/*
 * ConditionPage
 *
 * Renders a single condition's dedicated page. The content itself
 * lives in conditionsData.jsx; this component is purely presentation +
 * calls-to-action.
 *
 * Primary CTAs:
 *   - "Book an expert consultation" -> opens the Connect-with-our-team
 *     modal (the same one triggered from the header phone pill).
 *     Framed as a curated hand-off to a leading psychiatrist in this
 *     condition, not a self-serve calendar.
 *   - "Take a self-assessment" -> navigates to /assessment/:slug. That
 *     route is already auth-gated on ConsumerLayout, so unauthenticated
 *     users are routed through login automatically. We also pass a
 *     `redirect` parameter so they return here after signing in.
 *
 * Props:
 *   - slug: string (from the URL param)
 *   - onContactClick({ mode }): called when any CTA wants to open the
 *     contact modal. Parent decides call vs. whatsapp.
 */

const ACCENT_TO_CSS = {
  indigo: 'cn-accent-indigo',
  violet: 'cn-accent-violet',
  teal: 'cn-accent-teal',
  amber: 'cn-accent-amber',
  rose: 'cn-accent-rose',
  sky: 'cn-accent-sky',
  slate: 'cn-accent-slate',
}

export default function ConditionPage({ slug, onContactClick }) {
  const navigate = useNavigate()
  const condition = getConditionBySlug(slug)

  if (!condition) {
    return (
      <main id="main" className="as-section as-section-alt">
        <Container>
          <div className="as-max-w-3">
            <h1 className="as-h2">Condition not found</h1>
            <p className="as-lede">
              Explore the full list of conditions we support, or return to the home page.
            </p>
            <div className="as-mt-6 as-flex as-gap-3 as-wrap">
              <Link to="/" className="as-btn as-btn-primary">
                Go to home
              </Link>
              <Link to="/#conditions" className="as-btn as-btn-secondary">
                See all conditions
              </Link>
            </div>
          </div>
        </Container>
      </main>
    )
  }

  const accentClass = ACCENT_TO_CSS[condition.accent] || ACCENT_TO_CSS.indigo
  const {
    title,
    tagline,
    Icon,
    overview,
    signs,
    whenToReachOut,
    therapyRole,
    medicationRole,
    assessmentSlugs = [],
    faqs = [],
  } = condition

  const openConsultCall = () => onContactClick?.({ mode: 'call' })
  const openConsultWhatsapp = () => onContactClick?.({ mode: 'whatsapp' })

  const startSelfAssessment = () => {
    if (!assessmentSlugs.length) return
    // /assessment/:slug is protected server-side — an unauthenticated
    // visitor hits the ProtectedRoute wrapper and is routed through the
    // ClubbedAuth modal first. We pass a post-login redirect so they
    // return right here after signing in.
    const target = `/assessment/${assessmentSlugs[0]}`
    const url = `${target}?redirect=${encodeURIComponent(`/conditions/${condition.slug}`)}`
    navigate(url)
  }

  return (
    <main id="main" className={`cn-page ${accentClass}`}>
      {/* ── Hero ──────────────────────────────────────── */}
      <section className="cn-hero">
        <Container>
          <div className="cn-hero-grid">
            <div className="cn-hero-copy">
              <div className="cn-eyebrow">We help with</div>
              <h1 className="cn-h1">{title}</h1>
              {tagline ? <p className="cn-tagline">{tagline}</p> : null}
              <div className="cn-cta-row">
                <button
                  type="button"
                  className="cn-cta cn-cta-primary"
                  onClick={openConsultCall}
                >
                  Book an expert consultation
                </button>
                <button
                  type="button"
                  className="cn-cta cn-cta-ghost"
                  onClick={openConsultWhatsapp}
                >
                  Or chat on WhatsApp
                </button>
              </div>
              <p className="cn-cta-sub">
                We’ll call (or WhatsApp you — your choice) and set an appointment with a
                leading psychiatrist in this condition.
              </p>
            </div>
            <div className="cn-hero-visual" aria-hidden="true">
              <div className="cn-hero-mark">
                <Icon width={52} height={52} />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Overview ──────────────────────────────────── */}
      <section className="cn-section">
        <Container>
          <div className="cn-section-grid">
            <div>
              <div className="cn-section-eyebrow">Overview</div>
              <h2 className="cn-h2">Understanding {title.toLowerCase()}</h2>
            </div>
            <div className="cn-prose">
              {overview.map((p, i) => (
                <p key={`ov-${i}`}>{p}</p>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── Signs to notice ───────────────────────────── */}
      <section className="cn-section cn-section-alt">
        <Container>
          <div className="cn-section-grid">
            <div>
              <div className="cn-section-eyebrow">Common signs</div>
              <h2 className="cn-h2">What people typically notice</h2>
              <p className="cn-section-sub">
                Not a diagnostic checklist — a map of patterns that often bring people in.
              </p>
            </div>
            <ul className="cn-sign-list" aria-label="Common signs">
              {signs.map((s, i) => (
                <li key={`sign-${i}`}>
                  <span className="cn-check" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                         strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* ── When to reach out ─────────────────────────── */}
      {whenToReachOut?.length ? (
        <section className="cn-section">
          <Container>
            <div className="cn-section-grid">
              <div>
                <div className="cn-section-eyebrow">When to reach out</div>
                <h2 className="cn-h2">Earlier is always better</h2>
                <p className="cn-section-sub">
                  You don’t need to wait for things to get worse to be entitled to care.
                </p>
              </div>
              <ul className="cn-reach-list">
                {whenToReachOut.map((s, i) => (
                  <li key={`r-${i}`}>
                    <span className="cn-dot" aria-hidden="true" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Self-assessment CTA ───────────────────────── */}
      {assessmentSlugs.length ? (
        <section className="cn-section cn-section-alt">
          <Container>
            <div className="cn-self-test">
              <div>
                <div className="cn-section-eyebrow">Self-assessment</div>
                <h2 className="cn-h2">Take a validated self-screen</h2>
                <p className="cn-section-sub">
                  A short, confidential, clinician-validated questionnaire you can do on your
                  own. It isn’t a diagnosis — it’s a starting point, and the team can use the
                  result during your consultation. You’ll be asked to sign in so your result
                  is saved securely and only you (and the care team) can see it.
                </p>
              </div>
              <div className="cn-self-test-actions">
                <button
                  type="button"
                  className="cn-cta cn-cta-primary"
                  onClick={startSelfAssessment}
                >
                  Start the self-assessment
                </button>
                <div className="cn-self-test-note">Takes 3–10 minutes · Private</div>
              </div>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Care philosophy: therapy & medication ─────── */}
      <section className="cn-section">
        <Container>
          <div className="cn-philo-head">
            <div className="cn-section-eyebrow">Our clinical approach</div>
            <h2 className="cn-h2">Therapy and medication both have a role</h2>
            <p className="cn-section-sub">
              We’re neutral by design: the plan depends on your presentation, not on
              ideology. Here’s how each contributes.
            </p>
          </div>
          <div className="cn-philo-grid">
            <div className="cn-philo-card">
              <div className="cn-philo-label">Therapy</div>
              <p>{therapyRole}</p>
            </div>
            <div className="cn-philo-card">
              <div className="cn-philo-label">Medication</div>
              <p>{medicationRole}</p>
            </div>
            <div className="cn-philo-card cn-philo-emphasis">
              <div className="cn-philo-label">Medication adherence</div>
              <p>
                When medication is part of the plan, adherence is often the single largest
                factor in long-term outcomes. Our care team checks in on dosing, side-
                effects, and refills — so you’re not alone in managing it.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Care flow visualisation ───────────────────── */}
      <section className="cn-section cn-section-alt">
        <Container>
          <CareFlow
            title="What happens when you reach out"
            sub="No black box — here’s how care unfolds for this condition."
          />
        </Container>
      </section>

      {/* ── FAQ ───────────────────────────────────────── */}
      {faqs.length ? (
        <section className="cn-section">
          <Container>
            <div className="cn-section-grid">
              <div>
                <div className="cn-section-eyebrow">Questions</div>
                <h2 className="cn-h2">Before you book</h2>
              </div>
              <div className="cn-faq">
                {faqs.map((f, i) => (
                  <details key={`faq-${i}`} className="cn-faq-item">
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Final CTA ─────────────────────────────────── */}
      <section className="cn-cta-final">
        <Container>
          <div className="cn-cta-final-card">
            <h2 className="cn-h2">Ready to take the first step?</h2>
            <p>
              Book a call with our care team. We’ll match you with a leading psychiatrist
              in {title.toLowerCase()} and take it from there.
            </p>
            <div className="cn-cta-row">
              <button
                type="button"
                className="cn-cta cn-cta-primary"
                onClick={openConsultCall}
              >
                Book an expert consultation
              </button>
              <button
                type="button"
                className="cn-cta cn-cta-ghost"
                onClick={openConsultWhatsapp}
              >
                Chat on WhatsApp instead
              </button>
            </div>
          </div>
        </Container>
      </section>
    </main>
  )
}
